import { Injectable } from "@angular/core";

import { LocalStorageService } from "../local-storage/local-storage.service";
import { IBaseCard } from "src/app/views-interfaces";

export interface EncryptedCard {
    cardId: string;
    card: string;
    ts: number;
}

@Injectable({ providedIn: "root" })
export class CryptoService {
    secretKey: CryptoKey;
    private getKeyPromise;

    constructor() {}

    async getKey() {
        this.getKeyPromise ??= this.getKey_internal();
        await this.getKeyPromise;
        return this.secretKey;
    }

    async getKey_internal() {
        if (!this.secretKey) {
            const fromLocalStorage = globalThis.localStorage.getItem("phrases_v1");
            if (fromLocalStorage) {
                this.secretKey = await globalThis.crypto.subtle.importKey("raw", this.convertStringToArray(fromLocalStorage), "AES-GCM", true, [
                    "encrypt",
                    "decrypt",
                ]);
            }
        }
        if (!this.secretKey) {
            this.secretKey = await globalThis.crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
            LocalStorageService.setItem(
                "phrases_v1",
                this.convertArrToString(new Uint8Array(await globalThis.crypto.subtle.exportKey("raw", this.secretKey))),
            );
        }
        return this.secretKey;
    }

    async encrypt(msg) {
        let encryptedValue = msg;
        if (msg) {
            const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
            const encodedText = new TextEncoder().encode(JSON.stringify(msg));
            const array = new Uint8Array(await globalThis.crypto.subtle.encrypt({ name: "AES-GCM", iv: iv }, await this.getKey(), encodedText));
            const mergedArray = new Uint8Array(iv.length + array.byteLength);
            mergedArray.set(iv);
            mergedArray.set(array, iv.length);
            encryptedValue = {
                cardId: msg.cardId,
                card: this.convertArrToString(mergedArray),
                ts: msg._ts,
            };
        }
        return encryptedValue;
    }

    async decrypt(msg): Promise<IBaseCard> {
        let decryptedValue = msg;
        if (msg) {
            const cipherArray = this.convertStringToArray(msg.card);
            if (cipherArray && cipherArray.length !== 0) {
                const iv = cipherArray.subarray(0, 12);
                const encryptedArray = cipherArray.subarray(12);
                const decrypted = await globalThis.crypto.subtle.decrypt({ name: "AES-GCM", iv: iv }, await this.getKey(), encryptedArray);
                decryptedValue = JSON.parse(new TextDecoder().decode(decrypted));
            }
        }
        return decryptedValue;
    }

    convertArrToString(array: Uint8Array) {
        const tmpArray = [];
        array.forEach(function (element) {
            tmpArray.push(String.fromCodePoint(element));
        });
        return tmpArray.join("");
    }

    convertStringToArray(str: string) {
        if (!str) {
            return new Uint8Array();
        }
        const tmpArr = new Uint8Array(str.length);
        for (let index = 0; index < str.length; index++) tmpArr[index] = str.codePointAt(index);
        return tmpArr;
    }

    static bytesArrToHex(arr) {
        return [...arr].map((x) => x.toString(16).padStart(2, "0")).join("");
    }

    static async sha256(message: string) {
        const encoder = new TextEncoder();
        const data = encoder.encode(message);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        return new Uint8Array(hashBuffer);
    }

    static concat(arr1: Uint8Array, arr2: Uint8Array) {
        const mergedArray = new Uint8Array(arr1.length + arr2.length);
        mergedArray.set(arr1);
        mergedArray.set(arr2, arr1.length);
        return mergedArray;
    }

    static base64ToBytes(base64: string) {
        const binaryString = window.atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    }
}
